import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError} from "axios";
import {Session} from "../domain/session.entity";
import {createSessionRepository} from "../data/repositories";
import {SessionRepositoryV2} from "../data/repositories/session.repository.v2";
import {
  CreateSessionData,
  UpdateSessionData,
  PaginationParams,
  PaginatedResult,
  V2PaginatedResult,
  SessionSalesInsights,
  SessionCustomer,
} from "../data/mappers/session.mapper.types";

/**
 * Legacy query key constant for backward compatibility.
 * Used by existing code in src/react-query-hooks/session and related modules.
 * New hooks use this same key to ensure cache invalidation works across old/new code.
 */
export const GET_COACH_SESSION_OFFERINGS_QUERY_KEY = "COACH_SESSION_OFFERINGS";

/**
 * Query keys for session cache management.
 * Uses legacy key prefix for cache compatibility with existing hooks.
 */
export const SESSION_QUERY_KEYS = {
  all: [GET_COACH_SESSION_OFFERINGS_QUERY_KEY] as const,
  single: (id: number) => [GET_COACH_SESSION_OFFERINGS_QUERY_KEY, String(id)] as const,
  list: (page: number, perPage: number) =>
    [GET_COACH_SESSION_OFFERINGS_QUERY_KEY, "list", page, perPage] as const,
  salesInsights: (id: number) =>
    [GET_COACH_SESSION_OFFERINGS_QUERY_KEY, "sales-insights", id] as const,
  customers: (id: number, page: number, perPage: number) =>
    [GET_COACH_SESSION_OFFERINGS_QUERY_KEY, "customers", id, page, perPage] as const,
};

/**
 * Hook for fetching a single session by ID.
 *
 * @param id - The session ID to fetch
 * @param enabled - Whether the query should run (default: true)
 * @returns React Query result with Session entity
 *
 * @example
 * const { data: session, isLoading, error } = useSession(123);
 */
export const useSession = (id: number, enabled: boolean = true) => {
  const repository = createSessionRepository();

  return useQuery<Session, AxiosError<{message: string}>>({
    queryKey: SESSION_QUERY_KEYS.single(id),
    queryFn: () => repository.getById(id),
    enabled: enabled && id > 0,
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook for fetching a paginated list of sessions.
 *
 * @param params - Pagination parameters (page, perPage)
 * @param enabled - Whether the query should run (default: true)
 * @returns React Query result with PaginatedResult containing Session entities
 *
 * @example
 * const { data, isLoading, error } = useSessions({ page: 1, perPage: 10 });
 * // data.data contains Session[] array
 * // V1: data.meta contains { currentPage, perPage, total, lastPage }
 * // V2: data.total and data.hasNextPage
 */
export const useSessions = (params: PaginationParams, enabled: boolean = true) => {
  const repository = createSessionRepository();

  return useQuery<
    PaginatedResult<Session> | V2PaginatedResult<Session>,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: SESSION_QUERY_KEYS.list(params.page, params.perPage),
    queryFn: () => repository.getList(params),
    enabled,
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook for creating a new session.
 *
 * @returns React Query mutation object
 *
 * @example
 * const mutation = useCreateSession();
 * mutation.mutate({ name: 'New Session', ... });
 */
export const useCreateSession = () => {
  const queryClient = useQueryClient();
  const repository = createSessionRepository();

  return useMutation<Session, AxiosError<{message: string}>, CreateSessionData>({
    mutationFn: (data) => repository.create(data),
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
    onSuccess: () => {
      // Invalidate session list queries so they refetch with new session
      queryClient.invalidateQueries({queryKey: SESSION_QUERY_KEYS.all});
    },
  });
};

/**
 * Hook for deleting a session with optimistic updates.
 *
 * Implements optimistic UI:
 * - Immediately removes session from cache on mutate
 * - Restores session on error (rollback)
 * - Invalidates queries on settle for fresh data
 *
 * @returns React Query mutation object
 *
 * @example
 * const mutation = useDeleteSession();
 * mutation.mutate(123); // Session 123 removed from UI immediately
 */
export const useDeleteSession = () => {
  const queryClient = useQueryClient();
  const repository = createSessionRepository();

  return useMutation<void, AxiosError<{message: string}>, number, () => void>({
    mutationFn: (id) => repository.delete(id),
    onMutate: async (sessionId) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({queryKey: SESSION_QUERY_KEYS.all});

      // Snapshot all list queries for rollback
      const queriesSnapshot = queryClient.getQueriesData<
        PaginatedResult<Session> | V2PaginatedResult<Session>
      >({queryKey: SESSION_QUERY_KEYS.all});

      // Optimistically remove session from all list caches
      queryClient.setQueriesData<PaginatedResult<Session> | V2PaginatedResult<Session>>(
        {queryKey: SESSION_QUERY_KEYS.all},
        (oldData) => {
          // Skip if no data or not a paginated result (e.g., single session query)
          if (!oldData || !Array.isArray(oldData.data)) return oldData;

          const filteredSessions = oldData.data.filter(
            (session) => session.id !== sessionId
          );

          // Handle both V1 (meta) and V2 (flat) formats
          if ("meta" in oldData) {
            return {
              ...oldData,
              data: filteredSessions,
              meta: {
                ...oldData.meta,
                total: Math.max(0, oldData.meta.total - 1),
              },
            };
          }

          // V2 format
          return {
            ...oldData,
            data: filteredSessions,
            total: Math.max(0, oldData.total - 1),
          };
        }
      );

      // Return rollback function
      return () => {
        queriesSnapshot.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      };
    },
    onError: (error, _sessionId, rollback) => {
      message.error(error.response?.data?.message || error.message);
      // Restore cache to previous state
      if (rollback) {
        rollback();
      }
    },
    onSettled: () => {
      // Always invalidate to ensure consistency with server
      queryClient.invalidateQueries({queryKey: SESSION_QUERY_KEYS.all});
    },
  });
};

/**
 * Hook for fetching session sales insights (V2-only feature).
 *
 * Sales insights include total bookings and per-currency revenue.
 *
 * @param id - The session ID to fetch insights for
 * @param enabled - Whether the query should run (default: true)
 * @returns React Query result with SessionSalesInsights
 *
 * @example
 * const { data: insights, isLoading } = useSessionSalesInsights(123);
 * // insights.totalBookings, insights.totalRevenue[{currency, totalRevenue}]
 */
export const useSessionSalesInsights = (id: number, enabled: boolean = true) => {
  const repository = new SessionRepositoryV2();

  return useQuery<SessionSalesInsights, AxiosError<{message: string}>>({
    queryKey: SESSION_QUERY_KEYS.salesInsights(id),
    queryFn: () => repository.getSalesInsights(id),
    enabled: enabled && id > 0,
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook for updating an existing session.
 *
 * @returns React Query mutation object
 *
 * @example
 * const mutation = useUpdateSession();
 * mutation.mutate({ id: 123, name: 'Updated Session', ... });
 */
export const useUpdateSession = () => {
  const queryClient = useQueryClient();
  const repository = createSessionRepository();

  return useMutation<Session, AxiosError<{message: string}>, UpdateSessionData>({
    mutationFn: (data) => repository.update(data),
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
    onSuccess: () => {
      // Invalidate session queries so they refetch with updated data
      queryClient.invalidateQueries({queryKey: SESSION_QUERY_KEYS.all});
    },
  });
};

/**
 * Hook for fetching customers who booked a session (V2-only feature).
 *
 * @param id - The session ID to fetch customers for
 * @param params - Pagination parameters (page, perPage)
 * @param enabled - Whether the query should run (default: true)
 * @returns React Query result with V2PaginatedResult<SessionCustomer>
 *
 * @example
 * const { data, isLoading } = useSessionCustomers(123, { page: 1, perPage: 10 });
 * // data.data contains SessionCustomer[] array
 * // data.total, data.hasNextPage for pagination
 */
export const useSessionCustomers = (
  id: number,
  params: PaginationParams,
  enabled: boolean = true
) => {
  const repository = new SessionRepositoryV2();

  return useQuery<V2PaginatedResult<SessionCustomer>, AxiosError<{message: string}>>({
    queryKey: SESSION_QUERY_KEYS.customers(id, params.page, params.perPage),
    queryFn: () => repository.getCustomers(id, params),
    enabled: enabled && id > 0,
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook for exporting session customers as CSV (V2-only feature).
 *
 * Returns a Blob that can be used to trigger a download.
 *
 * @returns React Query mutation object
 *
 * @example
 * const mutation = useExportSessionCustomersCSV();
 * mutation.mutate(123, {
 *   onSuccess: (blob) => {
 *     const url = URL.createObjectURL(blob);
 *     // trigger download...
 *   }
 * });
 */
export const useExportSessionCustomersCSV = () => {
  const repository = new SessionRepositoryV2();

  return useMutation<Blob, AxiosError<{message: string}>, number>({
    mutationFn: (id) => repository.exportCustomersCSV(id),
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });
};
